import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  MenuItem,
  TextField,
} from '@mui/material'
import React, { useState } from 'react'
import useAxiosPrivate from '../../api/hooks/useAxiosPrivate'
import { PhonelinkLock } from '@mui/icons-material'
import useToastNotification from '../../hooks/useToastNotification'
import { useAuthContext } from '../../context/auth/AuthContextProvider'

type TwoFactorAuthenticationProps = {
  onClick: () => void
}

const TwoFactorAuthentication = ({ onClick }: TwoFactorAuthenticationProps) => {
  const { user, userSignOut } = useAuthContext()
  const axiosPrivate = useAxiosPrivate()
  const { showApiErrorMessage } = useToastNotification()
  const [openPopup, setOpenPopup] = useState<boolean>(false)
  const [qrCode, setQrCode] = useState<string>('')
  const [gaCode, setGaCode] = useState<string>('')
  const [disableCompleteButton, setDisableCompleteButton] =
    useState<boolean>(true)

  const handleClosePopup = () => {
    setOpenPopup(false)

    setQrCode('')
    setGaCode('')
  }

  const handleGaCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    const code = value.replace(/\D/g, '')
    setGaCode(code)

    if (code.length > 0) {
      setDisableCompleteButton(false)
    } else {
      setDisableCompleteButton(true)
    }
  }

  const createQrCode = async () => {
    if (user.twoFactorAuth) return

    onClick()

    await axiosPrivate
      .get('/api/google-authenticator/create')
      .then((response) => {
        setQrCode(response.data)
        setOpenPopup(true)
      })
      .catch((error) => {
        showApiErrorMessage(
          error,
          'Something went wrong while creating the QR code. Please refresh the page and try again.'
        )
      })
  }

  const handleComplete = async () => {
    await axiosPrivate
      .post('/api/google-authenticator/validate-activation', {
        ga_code: gaCode,
      })
      .then(() => {
        userSignOut()
      })
      .catch((error) => {
        showApiErrorMessage(
          error,
          'Something went wrong while validating your activation code. Please refresh the page and try again.'
        )
      })
  }

  return (
    <React.Fragment>
      <MenuItem onClick={createQrCode}>
        <ListItemIcon>
          <PhonelinkLock fontSize="small" />
        </ListItemIcon>
        {user.twoFactorAuth ? '2FA Configured' : 'Setup 2FA'}
      </MenuItem>
      <Dialog open={openPopup} onClose={handleClosePopup}>
        <DialogTitle sx={{ fontWeight: 700 }}>Setup 2FA</DialogTitle>

        <DialogContent>
          <Box>
            <small>
              To complete the 2FA activation, please add the QR code using
              Google Authenticator app, and then insert the code given below
            </small>
          </Box>
          <Box textAlign={'center'}>
            <img src={qrCode} alt="Google Authenticator QR Code" />
          </Box>
          <Box textAlign={'center'}>
            <TextField
              label="Insert code"
              value={gaCode}
              size="small"
              onChange={handleGaCodeChange}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ mr: 3, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={handleClosePopup}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={disableCompleteButton}
            onClick={handleComplete}
          >
            Complete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export default TwoFactorAuthentication
